import { hasCompatiblePermission } from '#app/permissions/landlord-permission-compat'
import type { UnifiedIconName } from './ui/unified-icon'

export interface DashboardNavItem {
	icon: UnifiedIconName
	/** Icon shown when the row is the active route; falls back to `icon`. */
	activeIcon?: UnifiedIconName
	name: string
	url: string
	badge?: string | number
	isActive?: boolean
	/**
	 * Single required permission. Kept for backward compat — prefer
	 * `requirePermission` (array) for new items. Interpreted as "user must
	 * have this permission OR `full_access`".
	 */
	permission?: string
	/**
	 * User must have at least one of these permission names (OR semantics).
	 * `full_access` always satisfies any permission gate.
	 */
	requirePermission?: string[]
	/**
	 * User must have at least one of these role names (OR semantics).
	 * Used for items that should be role-restricted regardless of
	 * granular permissions (e.g. "Roles & Permissions" → `tenant_owner`).
	 */
	requireRole?: string[]
	category?: string
	comingSoon?: boolean
	/**
	 * Optional child nav items, rendered as a sub-menu below this item when the
	 * current route is within this item's `url` (e.g. the Billing dashboard's
	 * sections). Children inherit the parent's visibility gate.
	 */
	children?: DashboardNavItem[]
	/**
	 * Opt-in module rollout key (e.g. `lease_generator`). Hidden unless the
	 * landlord org currently has that module. Absent → not module-gated.
	 */
	requireModule?: string
}

// Each nav item declares its own `requireRole` / `requirePermission` gate.
// Apex roles (superadmin, tenant_owner) are expected to hold either the
// `full_access` permission or the relevant role tag — the seeders in
// `app/models/landlord-roles.server.ts` and `app/models/platform-roles.server.ts`
// guarantee this. Custom roles get filtered correctly without a bypass.

// ─────────────────────────────────────────────────────────────
// SuperAdmin navigation
// ─────────────────────────────────────────────────────────────

export const superAdminNavItems: DashboardNavItem[] = [
	{
		icon: 'nav-dashboard',
		activeIcon: 'nav-dashboard-active',
		name: 'Super Dashboard',
		url: '/dashboard',
		requireRole: ['superadmin'],
		category: 'main',
	},
	{
		icon: 'nav-user-management',
		activeIcon: 'nav-user-management-active',
		name: 'Landlord Organizations',
		url: '/superadmin/tenants',
		requirePermission: ['tenant_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'Module Access',
		url: '/superadmin/module-access',
		requirePermission: ['tenant_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'Data Retention',
		url: '/superadmin/data-retention',
		requirePermission: ['tenant_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-user-management',
		activeIcon: 'nav-user-management-active',
		name: 'All Users',
		url: '/superadmin/users',
		requirePermission: ['user_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-user-management',
		activeIcon: 'nav-user-management-active',
		name: 'Platform Staff',
		url: '/superadmin/admins',
		requirePermission: ['user_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-roles',
		activeIcon: 'nav-roles-active',
		name: 'Roles & Permissions',
		url: '/superadmin/roles',
		requirePermission: ['role_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'nav-properties',
		activeIcon: 'nav-properties-active',
		name: 'Properties',
		url: '/properties',
		requirePermission: ['full_access'],
		category: 'management',
	},
	{
		icon: 'nav-applications',
		activeIcon: 'nav-applications-active',
		name: 'All Applications',
		url: '/superadmin/applications',
		requirePermission: ['full_access'],
		category: 'management',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'System Settings',
		url: '/superadmin/settings',
		requirePermission: ['full_access'],
		category: 'settings',
	},
	{
		icon: 'nav-applications',
		activeIcon: 'nav-applications-active',
		name: 'Leases',
		url: '/superadmin/leases',
		requirePermission: ['full_access'],
		category: 'management',
	},
	{
		icon: 'nav-billing',
		activeIcon: 'nav-billing-active',
		name: 'Billing & Credits',
		url: '/superadmin/billing',
		requirePermission: ['full_access'],
		category: 'settings',
		children: [
			{
				icon: 'nav-dashboard',
				activeIcon: 'nav-dashboard-active',
				name: 'Overview',
				url: '/superadmin/billing',
			},
			{
				icon: 'nav-user-management',
				activeIcon: 'nav-user-management-active',
				name: 'Landlords',
				url: '/superadmin/billing/landlords',
			},
			{
				icon: 'nav-applications',
				activeIcon: 'nav-applications-active',
				name: 'Transactions',
				url: '/superadmin/billing/transactions',
			},
			{
				icon: 'nav-settings',
				activeIcon: 'nav-settings-active',
				name: 'Operations',
				url: '/superadmin/billing/operations',
			},
			{
				icon: 'nav-settings',
				activeIcon: 'nav-settings-active',
				name: 'Settings',
				url: '/superadmin/billing/settings',
			},
		],
	},
	{
		// Superadmin ▸ LMR Operations (19 Oct 2026): is LMR working, who needs
		// help, what is being collected — across every landlord.
		icon: 'nav-lmr',
		activeIcon: 'nav-lmr-active',
		name: 'LMR Operations',
		url: '/superadmin/lmr-operations',
		requirePermission: ['full_access'],
		category: 'settings',
	},
	{
		icon: 'nav-notifications',
		activeIcon: 'nav-notifications-active',
		name: 'Notifications',
		url: '/superadmin/notifications',
		requirePermission: ['full_access'],
		category: 'settings',
	},
	{
		icon: 'nav-audit-log',
		activeIcon: 'nav-audit-log-active',
		name: 'Audit Log',
		url: '/superadmin/audit-log',
		requirePermission: ['full_access'],
		category: 'settings',
	},
]

// ─────────────────────────────────────────────────────────────
// Admin (platform-admin-but-not-super) navigation
// ─────────────────────────────────────────────────────────────

export const adminNavItems: DashboardNavItem[] = [
	{
		icon: 'nav-dashboard',
		activeIcon: 'nav-dashboard-active',
		name: 'Dashboard',
		url: '/dashboard',
		requireRole: ['admin'],
		category: 'main',
	},
	{
		// Portfolio-wide application view for admins (same surface tenant_owners
		// see). The loader on /landlord/applications already accepts the
		// `admin` role, so this just exposes it in the sidebar.
		icon: 'nav-applications',
		activeIcon: 'nav-applications-active',
		name: 'All Applications',
		url: '/landlord/applications',
		requireRole: ['admin'],
		category: 'main',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'nav-support',
		activeIcon: 'nav-support-active',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
]

// ─────────────────────────────────────────────────────────────
// Landlord navigation — gated against the real 8 landlord permissions
// declared in app/utils/landlord-permissions.ts
// ─────────────────────────────────────────────────────────────

export const landlordNavItems: DashboardNavItem[] = [
	{
		icon: 'nav-dashboard',
		activeIcon: 'nav-dashboard-active',
		name: 'Dashboard',
		url: '/dashboard',
		category: 'main',
	},
	{
		// Sheet: Full for Owner / PM / Agent / Viewer. Scope filters the list.
		icon: 'nav-applications',
		activeIcon: 'nav-applications-active',
		name: 'All Applications',
		url: '/landlord/applications',
		category: 'main',
	},
	{
		icon: 'nav-leases',
		activeIcon: 'nav-leases-active',
		name: 'Leases',
		url: '/landlord/leases',
		category: 'main',
		requireModule: 'lease_generator',
	},
	{
		// Sheet: Full for all roles; server scopes the portfolio.
		icon: 'nav-properties',
		activeIcon: 'nav-properties-active',
		name: 'Properties',
		url: '/properties',
		category: 'main',
	},
	{
		// LMR (TT-105) ships before rent collection (TT-119), which brings its
		// own Rent entry (icons: nav-rent / nav-rent-active). Every
		// landlord-side role can look; acting needs spending authority,
		// checked on the page and in every action.
		icon: 'nav-lmr',
		activeIcon: 'nav-lmr-active',
		name: 'LMR',
		url: '/lmr',
		category: 'main',
		// Superadmin ▸ Module Access ▸ LMR (per organization)
		requireModule: 'lmr',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'nav-support',
		activeIcon: 'nav-support-active',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
	// Billing, Notifications live in Settings tabs (`/settings?tab=...`).
]

// ─────────────────────────────────────────────────────────────
// Tenant navigation
// ─────────────────────────────────────────────────────────────

export const tenantNavItems: DashboardNavItem[] = [
	{
		icon: 'nav-dashboard',
		activeIcon: 'nav-dashboard-active',
		name: 'Dashboard',
		url: '/dashboard',
		category: 'main',
	},
	{
		// "Applications" (tenant) reuses the All Applications icon pair.
		icon: 'nav-applications',
		activeIcon: 'nav-applications-active',
		name: 'Applications',
		url: '/applications',
		category: 'applications',
	},
	{
		icon: 'nav-settings',
		activeIcon: 'nav-settings-active',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'nav-support',
		activeIcon: 'nav-support-active',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
]

// Secondary navigation items (common for all)
export const secondaryNavItems: DashboardNavItem[] = []

// ─────────────────────────────────────────────────────────────
// Filtering
// ─────────────────────────────────────────────────────────────

function hasRequiredRole(item: DashboardNavItem, userRoles: string[]): boolean {
	if (!item.requireRole || item.requireRole.length === 0) return true
	return item.requireRole.some((r) => userRoles.includes(r))
}

function hasRequiredPermission(
	item: DashboardNavItem,
	userPermissions: string[],
): boolean {
	const needed =
		item.requirePermission ?? (item.permission ? [item.permission] : [])
	if (needed.length === 0) return true
	try {
		return needed.some((p) => hasCompatiblePermission(userPermissions, p))
	} catch {
		// Never blank the whole sidebar if compat check misbehaves.
		return (
			userPermissions.includes('full_access') ||
			needed.some((p) => userPermissions.includes(p))
		)
	}
}

function hasRequiredModule(
	item: DashboardNavItem,
	accessibleModules: ReadonlySet<string> | undefined,
): boolean {
	if (!item.requireModule) return true
	// While modules are still loading, hide gated items rather than flash them.
	if (!accessibleModules) return false
	return accessibleModules.has(item.requireModule)
}

/**
 * Return the list of nav items visible to a user.
 *
 * An item is visible iff **every** gate it declares is satisfied:
 *   - `requireRole`   → user has at least one listed role
 *   - `requirePermission` / `permission` → user has at least one listed
 *     permission (or holds `full_access`)
 *   - `requireModule` → landlord org has that module (when modules provided)
 *
 * No role-based bypass — superadmin and tenant_owner pass gates because
 * they hold `full_access` (superadmin) or the role tags listed on each
 * item (tenant_owner). Custom roles are filtered exactly by their grants.
 */
function filterItems(
	items: DashboardNavItem[],
	userRoles: string[],
	userPermissions: string[],
	accessibleModules?: ReadonlySet<string>,
): DashboardNavItem[] {
	return items.filter(
		(item) =>
			hasRequiredRole(item, userRoles) &&
			hasRequiredPermission(item, userPermissions) &&
			hasRequiredModule(item, accessibleModules),
	)
}

/**
 * Get the nav set for the user's primary role, filtered by role + permission
 * gates. Uses userType as primary selector for custom role support.
 */
export function getDashboardNavItems(
	userRoles: string[],
	userPermissions: string[] = [],
	userType?: string,
	accessibleModules?: ReadonlySet<string>,
): DashboardNavItem[] {
	if (userRoles.includes('superadmin')) {
		return filterItems(
			superAdminNavItems,
			userRoles,
			userPermissions,
			accessibleModules,
		)
	}
	if (userRoles.includes('admin')) {
		return filterItems(
			adminNavItems,
			userRoles,
			userPermissions,
			accessibleModules,
		)
	}
	// Use userType as primary selector. Built-in Owner + custom landlord
	// roles (userType landlord) use the landlord nav set.
	const isLandlordSide =
		userType === 'landlord' ||
		userRoles.includes('landlord') ||
		userRoles.includes('tenant_owner') ||
		userRoles.includes('property_manager') ||
		userRoles.includes('leasing_agent') ||
		userRoles.includes('viewer') ||
		// Custom landlord roles often only appear as role name strings; if the
		// user holds any landlord settings/apps permission, stay on landlord nav.
		userPermissions.some(
			(p) =>
				p === 'full_access' ||
				p.startsWith('applications.') ||
				p.startsWith('properties.') ||
				p.startsWith('settings.') ||
				p.startsWith('nav.') ||
				p === 'roles.manage' ||
				p === 'users.edit_assign' ||
				p === 'data.export',
		)
	if (isLandlordSide) {
		return filterItems(
			landlordNavItems,
			userRoles,
			userPermissions,
			accessibleModules,
		)
	}
	return filterItems(
		tenantNavItems,
		userRoles,
		userPermissions,
		accessibleModules,
	)
}

// Group navigation items by category
export function groupNavItemsByCategory(
	items: DashboardNavItem[],
): Record<string, DashboardNavItem[]> {
	return items.reduce(
		(groups, item) => {
			const category = item.category || 'other'
			if (!groups[category]) {
				groups[category] = []
			}
			groups[category].push(item)
			return groups
		},
		{} as Record<string, DashboardNavItem[]>,
	)
}

// Get role display information
export function getRoleDisplayInfo(role: string): {
	name: string
	color: string
	icon: UnifiedIconName
} {
	const roleInfo = {
		superadmin: {
			name: 'Super Admin',
			color: 'red',
			icon: 'shield-check' as UnifiedIconName,
		},
		admin: {
			name: 'Admin',
			color: 'blue',
			icon: 'user-check' as UnifiedIconName,
		},
		landlord: {
			name: 'Landlord',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		tenant_owner: {
			name: 'Landlord',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		property_manager: {
			name: 'Property Manager',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		leasing_agent: {
			name: 'Leasing Agent',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		viewer: {
			name: 'Viewer',
			color: 'gray',
			icon: 'person' as UnifiedIconName,
		},
		tenant: {
			name: 'Tenant',
			color: 'purple',
			icon: 'person' as UnifiedIconName,
		},
	}

	return (
		roleInfo[role as keyof typeof roleInfo] || {
			name: role,
			color: 'gray',
			icon: 'person' as UnifiedIconName,
		}
	)
}
